/**
 * Module: Authentication & Authorization
 * Generated from backend DTOs
 * Location: src/modules/auth/dto
 * Source: AUDIT_BACKEND/A3_TYPESCRIPT_TYPES/auth.types.ts
 * 
 * КРИТИЧНО: Эти типы соответствуют структуре ответов бекенда версии 1.3.0
 * Используйте эти типы для всех API запросов к бекенду
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Internal roles for Event Master platform staff
 * Used for platform employees and administrators
 */
export enum InternalRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  PLATFORM_ADMIN = 'PLATFORM_ADMIN',
  PLATFORM_MANAGER = 'PLATFORM_MANAGER',
  CONTENT_MODERATOR = 'CONTENT_MODERATOR',
  SUPPORT_AGENT = 'SUPPORT_AGENT',
  SECURITY_ADMIN = 'SECURITY_ADMIN',
  TECHNICAL_SUPPORT = 'TECHNICAL_SUPPORT',
  CUSTOMER_SUCCESS = 'CUSTOMER_SUCCESS',
  PRODUCT_MANAGER = 'PRODUCT_MANAGER',
  DEVELOPER = 'DEVELOPER',
  QA_ENGINEER = 'QA_ENGINEER',
  OPERATIONS_MANAGER = 'OPERATIONS_MANAGER',
  MONITORING_ENGINEER = 'MONITORING_ENGINEER',
  COMMUNITY_MANAGER = 'COMMUNITY_MANAGER',
}

/**
 * Access levels for internal roles
 */
export enum InternalRoleLevel {
  LEVEL_1 = 1, // SUPER_ADMIN
  LEVEL_2 = 2, // PLATFORM_ADMIN, SECURITY_ADMIN
  LEVEL_3 = 3, // PRODUCT_MANAGER, OPERATIONS_MANAGER
  LEVEL_4 = 4, // TECHNICAL_SUPPORT, CUSTOMER_SUCCESS
  LEVEL_5 = 5, // DEVELOPER, QA_ENGINEER, MONITORING_ENGINEER
  LEVEL_6 = 6, // CONTENT_MODERATOR, COMMUNITY_MANAGER
}

/**
 * Event roles for users participating in events
 */
export enum EventRole {
  ORGANIZER = 'organizer',
  CO_ORGANIZER = 'co_organizer',
  EVENT_ADMIN = 'event_admin',
  PARTICIPANT = 'participant',
  SPEAKER = 'speaker',
  SUPPLIER = 'supplier',
  EXECUTOR = 'executor',
}

/**
 * Event role categories
 */
export enum EventRoleCategory {
  MANAGEMENT = 'management',
  PARTICIPATION = 'participation',
  SERVICE = 'service',
}

/**
 * User account status
 */
export enum UserStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
  BLOCKED = 'blocked',
}

/**
 * Role type discriminator
 */
export enum RoleType {
  INTERNAL = 'internal',
  EVENT = 'event',
}

/**
 * Two-factor authentication methods
 */
export type TwoFactorMethod = 'app' | 'sms' | 'email';

// ============================================================================
// PERMISSIONS
// ============================================================================

/**
 * Permissions for internal roles
 */
export enum InternalPermission {
  // System permissions
  SYSTEM_READ = 'system:read',
  SYSTEM_WRITE = 'system:write',
  SYSTEM_DELETE = 'system:delete',
  SYSTEM_MANAGE = 'system:manage',

  // User management
  USERS_READ = 'users:read',
  USERS_WRITE = 'users:write',
  USERS_DELETE = 'users:delete',
  USERS_MANAGE_ROLES = 'users:manage_roles',

  // Event management
  EVENTS_READ = 'events:read',
  EVENTS_WRITE = 'events:write',
  EVENTS_DELETE = 'events:delete',
  EVENTS_MANAGE_PARTICIPANTS = 'events:manage_participants',

  // Content management
  CONTENT_READ = 'content:read',
  CONTENT_WRITE = 'content:write',
  CONTENT_DELETE = 'content:delete',
  CONTENT_MODERATE = 'content:moderate',

  // Analytics
  ANALYTICS_READ = 'analytics:read',
  ANALYTICS_EXPORT = 'analytics:export',

  // Notifications
  NOTIFICATIONS_READ = 'notifications:read',
  NOTIFICATIONS_WRITE = 'notifications:write',
  NOTIFICATIONS_MANAGE = 'notifications:manage',

  // Finance
  FINANCE_READ = 'finance:read',
  FINANCE_WRITE = 'finance:write',
  FINANCE_MANAGE = 'finance:manage',

  // Reports
  REPORTS_READ = 'reports:read',
  REPORTS_WRITE = 'reports:write',
  REPORTS_EXPORT = 'reports:export',
}

/**
 * Permissions for event roles
 */
export enum EventPermission {
  // Event management
  EVENT_READ = 'event:read',
  EVENT_WRITE = 'event:write',
  EVENT_DELETE = 'event:delete',
  EVENT_MANAGE = 'event:manage',

  // Participant management
  PARTICIPANTS_READ = 'participants:read',
  PARTICIPANTS_WRITE = 'participants:write',
  PARTICIPANTS_DELETE = 'participants:delete',
  PARTICIPANTS_MANAGE = 'participants:manage',

  // Event content management
  EVENT_CONTENT_READ = 'event_content:read',
  EVENT_CONTENT_WRITE = 'event_content:write',
  EVENT_CONTENT_DELETE = 'event_content:delete',
  EVENT_CONTENT_MODERATE = 'event_content:moderate',

  // Schedule management
  SCHEDULE_READ = 'schedule:read',
  SCHEDULE_WRITE = 'schedule:write',
  SCHEDULE_DELETE = 'schedule:delete',
  SCHEDULE_MANAGE = 'schedule:manage',

  // Resource management
  RESOURCES_READ = 'resources:read',
  RESOURCES_WRITE = 'resources:write',
  RESOURCES_DELETE = 'resources:delete',
  RESOURCES_MANAGE = 'resources:manage',

  // Event finance
  EVENT_FINANCE_READ = 'event_finance:read',
  EVENT_FINANCE_WRITE = 'event_finance:write',
  EVENT_FINANCE_MANAGE = 'event_finance:manage',

  // Event analytics
  EVENT_ANALYTICS_READ = 'event_analytics:read',
  EVENT_ANALYTICS_EXPORT = 'event_analytics:export',
}

// ============================================================================
// INTERFACES - Authentication DTOs
// ============================================================================

/**
 * Login request
 * @validation email - Must be valid email format
 * @validation password - Minimum 6 characters
 */
export interface LoginDto {
  /** User email address */
  email: string;

  /** User password (minimum 6 characters) */
  password: string;

  /** Remember me flag for extended session */
  rememberMe?: boolean;
}

/**
 * User registration request
 */
export interface RegisterDto {
  /** User email address */
  email: string;

  /** User password */
  password: string;

  /** Full name (if firstName and lastName not provided) */
  name?: string;

  /** First name */
  firstName?: string;

  /** Last name */
  lastName?: string;

  /** User role */
  role?: InternalRole;

  /** Department */
  department?: string;

  /** Position */
  position?: string;

  /** Phone number */
  phone?: string;

  /** Password confirmation */
  confirmPassword?: string;
}

/**
 * Refresh token request
 */
export interface RefreshTokenDto {
  /** Refresh token for obtaining new access token */
  refreshToken: string;
}

/**
 * Switch event role request
 */
export interface SwitchEventRoleDto {
  /** Event ID */
  eventId: string;

  /** Role ID to switch to */
  roleId: string;
}

/**
 * Switch internal role request
 */
export interface SwitchInternalRoleDto {
  /** Role ID to switch to */
  roleId: string;
}

// ============================================================================
// INTERFACES - Role Types (BC-003)
// ============================================================================

/**
 * Role entity (from backend)
 */
export interface Role {
  /** Role ID (UUID) */
  id: string;

  /** Role name (e.g., SUPER_ADMIN, ORGANIZER) */
  name: string;

  /** Role display name */
  displayName?: string;

  /** Role description */
  description?: string;

  /** Role type (internal or event) */
  type: RoleType;

  /** Permissions granted by this role */
  permissions?: string[];
}

/**
 * Permission entity (from backend)
 */
export interface Permission {
  /** Permission ID (UUID) */
  id: string;

  /** Permission name (e.g., 'users:read', 'events:create') */
  name: string;

  /** Permission description */
  description?: string;

  /** Permission resource (e.g., 'users', 'events') */
  resource?: string;

  /** Permission action (e.g., 'read', 'write', 'delete') */
  action?: string;
}

/**
 * User Internal Role assignment
 * КРИТИЧНО: BC-003 - новая структура ролевой системы
 */
export interface UserInternalRole {
  /** Assignment ID */
  id: string;

  /** Role entity */
  role: Role;

  /** Is this role currently active */
  isActive: boolean;

  /** Assigned timestamp */
  assignedAt: string | Date;
}

/**
 * User Event Role assignment
 * КРИТИЧНО: BC-003 - новая структура ролевой системы
 */
export interface UserEventRole {
  /** Assignment ID */
  id: string;

  /** Event ID */
  eventId: string;

  /** Event name (optional, for display) */
  eventName?: string;

  /** Role entity */
  role: Role;

  /** Is this role currently active */
  isActive: boolean;

  /** Assigned timestamp */
  assignedAt: string | Date;
}

// ============================================================================
// INTERFACES - User Types (BC-003)
// ============================================================================

/**
 * User preferences
 */
export interface UserPreferences {
  /** UI theme preference */
  theme: 'light' | 'dark' | 'system';

  /** Language code (e.g., 'ru', 'en') */
  language: string;

  /** Enable notifications */
  notifications: boolean;

  /** Enable email notifications */
  emailNotifications: boolean;

  /** Timezone (e.g., 'Europe/Moscow') */
  timezone: string;
}

/**
 * User response from backend
 * КРИТИЧНО: BC-003 - новая структура с internalRoles, eventRoles, permissions
 * 
 * @see A6_BREAKING_CHANGES.json - BC-003 для детального описания
 */
export interface User {
  /** User ID (UUID) */
  id: string;

  /** Email address */
  email: string;

  /** First name */
  firstName: string;

  /** Last name */
  lastName: string;

  /** Full name (optional) */
  name?: string;

  /** Account status */
  status: UserStatus;

  /** Department */
  department?: string;

  /** Position */
  position?: string;

  /** Phone number */
  phone?: string;

  /** Avatar URL */
  avatar?: string;

  /** Age */
  age?: number;

  /** Gender */
  gender?: string;

  /**
   * Last login timestamp
   * @format ISO 8601 date string
   */
  lastLogin?: string | Date;

  /** Email verification status */
  isEmailVerified: boolean;

  /** User preferences */
  preferences: UserPreferences;

  /** Two-factor authentication enabled */
  twoFactorEnabled: boolean;

  /** Two-factor authentication method */
  twoFactorMethod?: string;

  /**
   * Created timestamp
   * @format ISO 8601 date string
   */
  createdAt: string | Date;

  /**
   * Updated timestamp
   * @format ISO 8601 date string
   */
  updatedAt: string | Date;

  /**
   * Deleted timestamp (soft delete)
   * @format ISO 8601 date string
   */
  deletedAt?: string | Date;

  // ============================================================================
  // BC-003: НОВАЯ РОЛЕВАЯ СИСТЕМА
  // ============================================================================

  /**
   * Internal roles (14 ролей платформы)
   * КРИТИЧНО: Используйте permissions для проверки прав, а НЕ прямую проверку ролей
   */
  internalRoles: UserInternalRole[];

  /**
   * Current active internal role ID
   */
  currentInternalRoleId: string | null;

  /**
   * Event roles (7 ролей в мероприятиях)
   * КРИТИЧНО: Используйте permissions для проверки прав, а НЕ прямую проверку ролей
   */
  eventRoles: UserEventRole[];

  /**
   * Current active event role ID
   */
  currentEventRoleId: string | null;

  /**
   * All permissions (74 разрешения)
   * КРИТИЧНО: Используйте hasPermission(user, 'permission:name') для проверки прав
   * 
   * @example
   * ```typescript
   * // ❌ НЕПРАВИЛЬНО - проверка роли
   * if (user.role === 'admin') { ... }
   * 
   * // ✅ ПРАВИЛЬНО - проверка разрешения
   * if (hasPermission(user, 'users:delete')) { ... }
   * ```
   */
  permissions: string[];
}

/**
 * User response (legacy, for backward compatibility)
 * @deprecated Use User instead
 */
export interface UserResponse extends Omit<User, 'internalRoles' | 'eventRoles' | 'permissions' | 'currentInternalRoleId' | 'currentEventRoleId'> {
  /** Internal role (legacy) */
  role: InternalRole;
}

/**
 * Authentication response with tokens
 */
export interface LoginResponse {
  /** Access token (JWT) */
  accessToken: string;

  /** Refresh token */
  refreshToken: string;

  /** User information */
  user: User;

  /** Token expiration time in seconds */
  expiresIn?: number;
}

/**
 * Get user roles response
 */
export interface GetUserRolesResponse {
  /** List of internal roles */
  internalRoles: UserInternalRole[];

  /** List of event roles */
  eventRoles: UserEventRole[];

  /** Current active internal role */
  currentInternalRole?: UserInternalRole;

  /** Current active event role */
  currentEventRole?: UserEventRole;

  /** Available roles for switching */
  availableRoles?: Array<{
    id: string;
    name: string;
    displayName: string;
    description?: string;
    permissions?: string[];
  }>;
}

